import { Duration } from "moment";
import { Project } from "../../pages/Project/ProjectDetails/types";
import {
  EeCodeRecord,
  CrewRole,
  Employee,
  ProjectTimeEntry,
  TaskRate,
  StateCounty,
} from "~/gql/types";
import { FormErrors } from "~/WorkDayApi/WorkDayApiType";
import { FORM_ERROR } from "final-form";
import _ from "lodash";

export type FormData = {
  hours: Duration | null;
  eeCode: EeCodeRecord | null;
  project: Pick<Project, "name" | "number" | "status"> | null;
  taskRate: TaskRate | null;
  taskName: string | null;
  notes: string | null;
  crewSize: string | null;
  myRole: CrewRole | null;
  otherCrewOne: Employee | null;
  otherCrewTwo: Employee | null;
  otherRole1: CrewRole | null;
  otherRole2: CrewRole | null;
  location: StateCounty | null;
  adminNotes: string | null;
  rateSheetName: string | null;
  saveAndClone: boolean;
  copyToDate: boolean;
  copyToDates: boolean;
  dailyHours: DailyHours[] | null;
  trainingUploadImageId: string | null;
  trainingUploadUrl: string | null;
  formErrors: FormErrors | null;
  employee: Employee;
  fieldSupervisor;
  leadPartyChief;
};

export type EmployeeRole =
  | "partyChief"
  | "instrumentTech"
  | "rodMan"
  | "fieldSupervisor"
  | "leadPartyChief";

export type HourType =
  | "asBuilt"
  | "constRestake"
  | "constStake"
  | "locate"
  | "boundary"
  | "clStake"
  | "rerouteStake"
  | "rainoutOther"
  | "travelHours";

export type DailyHours = {
  employee: Employee | null;
  role: EmployeeRole;
  asBuilt?: number;
  constRestake?: number;
  constStake?: number;
  locate?: number;
  boundary?: number;
  clStake?: number;
  rerouteStake?: number;
  rainoutOther?: number;
  travelHours?: number;
};

export type State = FormData & {
  taskRates: TaskRate[];
  loading: boolean;
  entry?: ProjectTimeEntry;
};

type TaggedAction<T extends string> = { tag: T };
type UpdateField = TaggedAction<"UpdateField"> & {
  fieldName: string;
  value: any;
};
type UpdateEeCode = TaggedAction<"UpdateEeCode"> & {
  eeCode: EeCodeRecord;
};
type LoadData = TaggedAction<"LoadData">;
type TaskRatesLoaded = TaggedAction<"TaskRatesLoaded"> & {
  taskRates: TaskRate[];
  rateSheetName: string;
};
type UpdateDailyHours = TaggedAction<"UpdateDailyHours"> & {
  hours: DailyHours;
};
type Submitted = TaggedAction<"Submitted">;

export type Action =
  | UpdateField
  | UpdateEeCode
  | LoadData
  | TaskRatesLoaded
  | UpdateDailyHours
  | Submitted;

const clearCrewTwo = {
  otherCrewTwo: null,
  otherRole2: null,
};

const clearCrew = {
  ...clearCrewTwo,
  otherCrewOne: null,
  otherRole1: null,
};

const clearTask = {
  taskRate: null,
  taskName: null,
  trainingUploadImageId: null,
  trainingUploadUrl: null,
};

export function reducer(state: State, action: Action): State {
  if (action.tag === "LoadData") {
    return { ...state, loading: true };
  }

  if (action.tag === "TaskRatesLoaded") {
    const matchedRate = action.taskRates.find(
      (x) => x?.name === state.taskName
    );

    const taskInfo =
      state.rateSheetName && action.rateSheetName != state.rateSheetName
        ? clearTask
        : { taskRate: matchedRate ?? null };

    return {
      ...state,
      ...taskInfo,
      loading: false,
      taskRates: action.taskRates,
      rateSheetName: action.rateSheetName,
    };
  }

  if (action.tag === "UpdateDailyHours") {
    const toUpdate = state.dailyHours!.find(
      (x) =>
        x.role === action.hours.role &&
        x.employee?.userPrincipalName ===
          action.hours.employee?.userPrincipalName
    );

    const dailyHours = state.dailyHours!.map((x) =>
      x === toUpdate ? { ...x, ...action.hours } : x
    );

    return {
      ...state,
      dailyHours,
      formErrors: validateHours(dailyHours, state),
    };
  }

  if (action.tag === "UpdateEeCode") {
    return { ...state, eeCode: action.eeCode };
  }

  if (action.tag === "Submitted") {
    return { ...state, loading: false };
  }

  if (action.tag === "UpdateField") {
    if (action.fieldName === "project") {
      if (!action.value) {
        return updateDailyHourRoles({
          ...state,
          project: null,
        });
      } else {
        const isOverhead = action.value.serviceDescription.isOverhead;
        return updateDailyHourRoles({
          ...state,
          project: action.value,
          location: isOverhead ? null : action.value.location,
        });
      }
    }

    if (action.fieldName === "taskRate") {
      if (!action.value) {
        return updateDailyHourRoles({
          ...state,
          ...clearTask,
        });
      } else {
        const requiresDaily = (action.value as TaskRate).requiresDaily;

        const crewMap = {
          1: {
            crewSize: 1,
            ...clearCrew,
            myRole: requiresDaily ? CrewRole.CrewChief : null,
          },
          2: {
            crewSize: 2,
            ...clearCrewTwo,
          },
          3: {
            crewSize: 3,
          },
        };

        const newState = {
          ...state,
          taskRate: action.value,
          taskName: action.value.name,
          ...crewMap[action.value.crewSize],
        };

        return updateDailyHourRoles(newState);
      }
    }

    const newState = {
      ...state,
      [action.fieldName]: action.value ?? null,
    };

    return updateDailyHourRoles(newState);
  }

  return state;
}

export function updateDailyHourRoles(state: State): State {
  if (state.dailyHours === null) {
    return state;
  }

  const roleMap: Record<CrewRole, EmployeeRole> = {
    CrewChief: "partyChief",
    IMan: "instrumentTech",
    Rodman: "rodMan",
  };

  const findHoursInState = (hoursRow: DailyHours) => {
    const foundByRoleAndName = state.dailyHours?.find(
      (x) =>
        x.role === hoursRow.role &&
        x.employee?.userPrincipalName === hoursRow.employee?.userPrincipalName
    );

    if (foundByRoleAndName) {
      return foundByRoleAndName;
    }

    return state.dailyHours?.find((x) => x.role === hoursRow.role) ?? {};
  };

  const dailyHours: DailyHours[] = _.chain([
    { employee: state.employee, role: roleMap[state.myRole!] },
    { employee: state.otherCrewOne, role: roleMap[state.otherRole1!] },
    { employee: state.otherCrewTwo, role: roleMap[state.otherRole2!] },
  ] as DailyHours[])
    .filter((x) => !!x.role && !!x.employee)
    .map((x) => ({ ...findHoursInState(x), ...x }))
    .orderBy((x) => (x.role === "partyChief" ? 0 : 1))
    .value();

  return {
    ...state,
    dailyHours,
    formErrors: validateHours(dailyHours, state),
  };
}

export const calculateHours = (data: DailyHours): number =>
  (data.asBuilt || 0.0) +
  (data.constRestake || 0.0) +
  (data.constStake || 0.0) +
  (data.locate || 0.0) +
  (data.boundary || 0.0) +
  (data.clStake || 0.0) +
  (data.rerouteStake || 0.0) +
  (data.rainoutOther || 0.0) +
  (data.travelHours || 0.0);

const validateHours = (
  dailyHours: DailyHours[] | null,
  state: State
): FormErrors | null => {
  const requiresDaily = state.taskRate?.requiresDaily;

  if (!requiresDaily || !dailyHours) {
    return null;
  }

  const formErrors: string[] = [];

  dailyHours.forEach((h) => {
    const calculatedHours = calculateHours(h);

    if (calculatedHours === 0) {
      const label = h.role.replace(/([A-Z])/g, " $1").toLowerCase();
      formErrors.push(`You must specify hours for ${label}.`);
    }
  });

  return formErrors.length > 0 ? { [FORM_ERROR]: formErrors.join(" ") } : null;
};
